import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Alert,
  Snackbar,
  IconButton,
  Collapse,
  FormControlLabel,
  Checkbox,
  LinearProgress,
  Tooltip,
  Avatar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Google as GoogleIcon,
  CloudQueue as DropboxIcon,
  Storage as OneDriveIcon,
  Chat as SlackIcon,
  GitHub as GitHubIcon,
  LinkedIn as LinkedInIcon,
  CalendarMonth as CalendarIcon,
  ContactPage as ContactsIcon,
  ExpandMore,
  ExpandLess,
  Refresh,
  LinkOff,
  Settings as SettingsIcon,
  CheckCircle,
  Schedule,
  Error as ErrorIcon,
  Security,
  Sync,
  Key,
  Webhook as WebhookIcon,
  Code as CodeIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  Visibility,
  VisibilityOff,
  Email as EmailIcon,
  Circle as CircleIcon,
} from '@mui/icons-material';

interface Integration {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  connected: boolean;
  category: 'storage' | 'productivity' | 'social';
  lastSync?: string;
  syncStatus?: 'success' | 'error' | 'syncing';
  permissions?: string[];
  features?: string[];
  connectedAccount?: string;
}

interface SyncSettings {
  syncContacts: boolean;
  syncCalendar: boolean;
  syncDrive: boolean;
  autoSync: boolean;
  syncInterval: number;
}

// ── Developer Space Sub-Component ─────────────────────────────────────────────
interface WebhookEntry { id: string; name: string; url: string; events: string[]; active: boolean; }
interface ApiKeyEntry  { id: string; name: string; key: string; created: string; lastUsed?: string; }

const DeveloperSpace: React.FC<{ snackbar: any; setSnackbar: any }> = ({ setSnackbar }) => {
  const [webhooks, setWebhooks] = useState<WebhookEntry[]>([
    { id: '1', name: 'New Email Webhook', url: 'https://example.com/webhook/email', events: ['email.received', 'email.sent'], active: true },
    { id: '2', name: 'Folder Change Hook', url: 'https://myapp.io/hooks/folder', events: ['folder.created', 'folder.deleted'], active: false },
  ]);
  const [apiKeys, setApiKeys] = useState<ApiKeyEntry[]>([
    { id: '1', name: 'My App Integration', key: 'crv_live_••••••••••••••••xxxx', created: '2024-01-10', lastUsed: '2024-04-15' },
  ]);
  const [webhookDialog, setWebhookDialog] = useState(false);
  const [apiKeyDialog, setApiKeyDialog] = useState(false);
  const [newWebhook, setNewWebhook] = useState({ name: '', url: '' });
  const [newKeyName, setNewKeyName] = useState('');
  const [showKey, setShowKey] = useState<Record<string, boolean>>({});

  const createWebhook = () => {
    if (!newWebhook.name || !newWebhook.url) return;
    setWebhooks(w => [...w, { id: Date.now().toString(), name: newWebhook.name, url: newWebhook.url, events: ['email.received'], active: true }]);
    setNewWebhook({ name: '', url: '' });
    setWebhookDialog(false);
    setSnackbar({ open: true, message: 'Webhook created', severity: 'success' });
  };

  const deleteWebhook = (id: string) => {
    setWebhooks(w => w.filter(x => x.id !== id));
    setSnackbar({ open: true, message: 'Webhook deleted', severity: 'info' });
  };

  const createApiKey = () => {
    if (!newKeyName) return;
    const key = `crv_live_${'x'.repeat(16)}`;
    setApiKeys(k => [...k, { id: Date.now().toString(), name: newKeyName, key, created: new Date().toISOString().split('T')[0] }]);
    setNewKeyName('');
    setApiKeyDialog(false);
    setSnackbar({ open: true, message: 'API key created', severity: 'success' });
  };

  const deleteApiKey = (id: string) => {
    setApiKeys(k => k.filter(x => x.id !== id));
    setSnackbar({ open: true, message: 'API key revoked', severity: 'info' });
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 500, mb: 1 }}>Developer Space</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Manage webhooks and API keys to connect your applications
      </Typography>

      {/* Webhooks */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
            <WebhookIcon fontSize="small" /> Webhooks
          </Typography>
          <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => setWebhookDialog(true)}>
            Add Webhook
          </Button>
        </Box>
        <Paper variant="outlined" sx={{ borderRadius: 2 }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>URL</TableCell>
                  <TableCell>Events</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {webhooks.length === 0 ? (
                  <TableRow><TableCell colSpan={5} align="center"><Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No webhooks configured</Typography></TableCell></TableRow>
                ) : webhooks.map(wh => (
                  <TableRow key={wh.id}>
                    <TableCell><Typography variant="body2" sx={{ fontWeight: 500 }}>{wh.name}</Typography></TableCell>
                    <TableCell><Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{wh.url}</Typography></TableCell>
                    <TableCell>{wh.events.map(e => <Chip key={e} label={e} size="small" sx={{ mr: 0.5 }} />)}</TableCell>
                    <TableCell>
                      <Switch size="small" checked={wh.active} onChange={() => setWebhooks(ws => ws.map(w => w.id === wh.id ? { ...w, active: !w.active } : w))} />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => deleteWebhook(wh.id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>

      {/* API Keys */}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
            <CodeIcon fontSize="small" /> API Keys
          </Typography>
          <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => setApiKeyDialog(true)}>
            Create Key
          </Button>
        </Box>
        <Paper variant="outlined" sx={{ borderRadius: 2 }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Key</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Last Used</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {apiKeys.length === 0 ? (
                  <TableRow><TableCell colSpan={5} align="center"><Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No API keys</Typography></TableCell></TableRow>
                ) : apiKeys.map(ak => (
                  <TableRow key={ak.id}>
                    <TableCell><Typography variant="body2" sx={{ fontWeight: 500 }}>{ak.name}</Typography></TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                          {showKey[ak.id] ? ak.key : ak.key.replace(/x+$/, '••••••••')}
                        </Typography>
                        <IconButton size="small" onClick={() => setShowKey(s => ({ ...s, [ak.id]: !s[ak.id] }))}>{showKey[ak.id] ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}</IconButton>
                        <Tooltip title="Copy"><IconButton size="small" onClick={() => { navigator.clipboard.writeText(ak.key); setSnackbar({ open: true, message: 'Copied!', severity: 'success' }); }}><CopyIcon fontSize="small" /></IconButton></Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell><Typography variant="caption">{ak.created}</Typography></TableCell>
                    <TableCell><Typography variant="caption">{ak.lastUsed ?? '—'}</Typography></TableCell>
                    <TableCell align="right">
                      <Tooltip title="Revoke"><IconButton size="small" color="error" onClick={() => deleteApiKey(ak.id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>

      {/* Create Webhook Dialog */}
      <Dialog open={webhookDialog} onClose={() => setWebhookDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add Webhook</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Name" value={newWebhook.name} onChange={e => setNewWebhook(w => ({ ...w, name: e.target.value }))} sx={{ mt: 1, mb: 2 }} size="small" />
          <TextField fullWidth label="Endpoint URL" placeholder="https://your-server.com/webhook" value={newWebhook.url} onChange={e => setNewWebhook(w => ({ ...w, url: e.target.value }))} size="small" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWebhookDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={createWebhook} disabled={!newWebhook.name || !newWebhook.url}>Create</Button>
        </DialogActions>
      </Dialog>

      {/* Create API Key Dialog */}
      <Dialog open={apiKeyDialog} onClose={() => setApiKeyDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Create API Key</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Key Name" placeholder="My App" value={newKeyName} onChange={e => setNewKeyName(e.target.value)} sx={{ mt: 1 }} size="small" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApiKeyDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={createApiKey} disabled={!newKeyName}>Create</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ── Channels Sub-Component ─────────────────────────────────────────────────────
interface Channel { id: string; name: string; email: string; type: 'inbox' | 'alias' | 'shared'; status: 'active' | 'inactive'; members?: number; }

const ChannelsSection: React.FC<{ snackbar: any; setSnackbar: any }> = ({ setSnackbar }) => {
  const [channels, setChannels] = useState<Channel[]>([
    { id: '1', name: 'Support', email: 'support@cruvzmail.com', type: 'shared', status: 'active', members: 3 },
    { id: '2', name: 'Sales', email: 'sales@cruvzmail.com', type: 'shared', status: 'active', members: 5 },
    { id: '3', name: 'No-Reply', email: 'noreply@cruvzmail.com', type: 'alias', status: 'inactive' },
  ]);
  const [addDialog, setAddDialog] = useState(false);
  const [newChannel, setNewChannel] = useState({ name: '', email: '', type: 'inbox' as Channel['type'] });

  const addChannel = () => {
    if (!newChannel.name || !newChannel.email) return;
    setChannels(c => [...c, { id: Date.now().toString(), ...newChannel, status: 'active' }]);
    setNewChannel({ name: '', email: '', type: 'inbox' });
    setAddDialog(false);
    setSnackbar({ open: true, message: 'Channel added', severity: 'success' });
  };

  const toggleStatus = (id: string) => {
    setChannels(c => c.map(ch => ch.id === id ? { ...ch, status: ch.status === 'active' ? 'inactive' : 'active' } : ch));
  };

  const deleteChannel = (id: string) => {
    setChannels(c => c.filter(ch => ch.id !== id));
    setSnackbar({ open: true, message: 'Channel removed', severity: 'info' });
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 500, mb: 1 }}>Channels</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Manage email channels — shared inboxes, aliases, and team channels
      </Typography>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setAddDialog(true)}>
          Add Channel
        </Button>
      </Box>

      <Paper variant="outlined" sx={{ borderRadius: 2 }}>
        <List disablePadding>
          {channels.map((ch, idx) => (
            <React.Fragment key={ch.id}>
              <ListItem
                secondaryAction={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Switch size="small" checked={ch.status === 'active'} onChange={() => toggleStatus(ch.id)} />
                    <Tooltip title="Remove"><IconButton size="small" color="error" onClick={() => deleteChannel(ch.id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                  </Box>
                }
              >
                <ListItemIcon>
                  <Avatar sx={{ width: 36, height: 36, bgcolor: ch.status === 'active' ? 'primary.main' : 'action.disabled' }}>
                    <EmailIcon fontSize="small" />
                  </Avatar>
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{ch.name}</Typography>
                      <Chip label={ch.type} size="small" variant="outlined" />
                      {ch.status === 'active'
                        ? <Chip size="small" label="Active" color="success" sx={{ height: 20 }} />
                        : <Chip size="small" label="Inactive" sx={{ height: 20 }} />}
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{ch.email}</Typography>
                      {ch.members && <Typography variant="caption" color="text.secondary"> · {ch.members} members</Typography>}
                    </Box>
                  }
                />
              </ListItem>
              {idx < channels.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>
      </Paper>

      <Dialog open={addDialog} onClose={() => setAddDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add Channel</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Channel Name" value={newChannel.name} onChange={e => setNewChannel(c => ({ ...c, name: e.target.value }))} sx={{ mt: 1, mb: 2 }} size="small" />
          <TextField fullWidth label="Email Address" value={newChannel.email} onChange={e => setNewChannel(c => ({ ...c, email: e.target.value }))} size="small" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={addChannel} disabled={!newChannel.name || !newChannel.email}>Add</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const IntegrationsSettings: React.FC = () => {
  const location = useLocation();
  // Determine active sub-section from URL
  const subPath = location.pathname.split('/settings/integrations')[1]?.replace(/^\//, '') || 'extensions';
  const activeSection = subPath || 'extensions';

  const [integrations, setIntegrations] = useState<Integration[]>([
    {
      id: 'google',
      name: 'Google Workspace',
      description: 'Connect Google Drive, Calendar, and Contacts',
      icon: <GoogleIcon sx={{ color: '#4285f4' }} />,
      connected: true,
      category: 'productivity',
      lastSync: '2024-12-19T10:30:00Z',
      syncStatus: 'success',
      permissions: ['Drive access', 'Calendar read/write', 'Contacts read'],
      features: ['Attach files from Drive', 'Calendar sync', 'Contact import'],
      connectedAccount: 'user@gmail.com',
    },
    {
      id: 'dropbox',
      name: 'Dropbox',
      description: 'Attach files from Dropbox and save attachments',
      icon: <DropboxIcon sx={{ color: '#0061ff' }} />,
      connected: false,
      category: 'storage',
      permissions: ['File read/write'],
      features: ['Attach files', 'Save attachments', 'Share links'],
    },
    {
      id: 'onedrive',
      name: 'Microsoft OneDrive',
      description: 'Attach files from OneDrive and save attachments',
      icon: <OneDriveIcon sx={{ color: '#0078d4' }} />,
      connected: false,
      category: 'storage',
      permissions: ['File read/write'],
      features: ['Attach files', 'Save attachments', 'Office integration'],
    },
    {
      id: 'slack',
      name: 'Slack',
      description: 'Forward emails to Slack channels and receive notifications',
      icon: <SlackIcon sx={{ color: '#4a154b' }} />,
      connected: true,
      category: 'productivity',
      lastSync: '2024-12-19T09:00:00Z',
      syncStatus: 'success',
      permissions: ['Send messages', 'Read channels'],
      features: ['Forward emails', 'Email notifications', 'Thread sync'],
      connectedAccount: 'workspace.slack.com',
    },
    {
      id: 'github',
      name: 'GitHub',
      description: 'Create issues from emails and track notifications',
      icon: <GitHubIcon sx={{ color: '#24292e' }} />,
      connected: false,
      category: 'productivity',
      permissions: ['Create issues', 'Read notifications'],
      features: ['Create issues', 'Track PRs', 'Notification emails'],
    },
    {
      id: 'linkedin',
      name: 'LinkedIn',
      description: 'Sync contacts and view professional profiles',
      icon: <LinkedInIcon sx={{ color: '#0077b5' }} />,
      connected: false,
      category: 'social',
      permissions: ['Profile read', 'Connections read'],
      features: ['Import contacts', 'View profiles', 'Network insights'],
    },
  ]);

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [connectDialogOpen, setConnectDialogOpen] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState<Integration | null>(null);
  const [apiKeyDialogOpen, setApiKeyDialogOpen] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [isConnecting, setIsConnecting] = useState(false);
  const [syncSettingsOpen, setSyncSettingsOpen] = useState(false);
  const [syncSettings, setSyncSettings] = useState<SyncSettings>({
    syncContacts: true,
    syncCalendar: true,
    syncDrive: true,
    autoSync: true,
    syncInterval: 15,
  });
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const handleToggleExpand = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const handleConnect = (integration: Integration) => {
    setSelectedIntegration(integration);
    
    // Some integrations need API key instead of OAuth
    if (['github'].includes(integration.id)) {
      setApiKeyDialogOpen(true);
    } else {
      setConnectDialogOpen(true);
    }
  };

  const handleDisconnect = (id: string) => {
    setIntegrations(integrations.map(i =>
      i.id === id ? { 
        ...i, 
        connected: false, 
        lastSync: undefined, 
        syncStatus: undefined,
        connectedAccount: undefined 
      } : i
    ));
    setSnackbar({ open: true, message: 'Integration disconnected', severity: 'info' });
  };

  const handleOAuthConnect = async () => {
    if (!selectedIntegration) return;
    
    setIsConnecting(true);
    
    // Simulate OAuth flow
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    setIntegrations(integrations.map(i =>
      i.id === selectedIntegration.id ? {
        ...i,
        connected: true,
        lastSync: new Date().toISOString(),
        syncStatus: 'success',
        connectedAccount: `user@${selectedIntegration.id}.com`,
      } : i
    ));
    
    setIsConnecting(false);
    setConnectDialogOpen(false);
    setSnackbar({ open: true, message: `${selectedIntegration.name} connected successfully!`, severity: 'success' });
  };

  const handleApiKeyConnect = async () => {
    if (!selectedIntegration || !apiKey) return;
    
    setIsConnecting(true);
    
    // Simulate API key validation
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    setIntegrations(integrations.map(i =>
      i.id === selectedIntegration.id ? {
        ...i,
        connected: true,
        lastSync: new Date().toISOString(),
        syncStatus: 'success',
        connectedAccount: 'API Key Connected',
      } : i
    ));
    
    setIsConnecting(false);
    setApiKeyDialogOpen(false);
    setApiKey('');
    setSnackbar({ open: true, message: `${selectedIntegration.name} connected successfully!`, severity: 'success' });
  };

  const handleSync = async (id: string) => {
    setIntegrations(integrations.map(i =>
      i.id === id ? { ...i, syncStatus: 'syncing' } : i
    ));
    
    // Simulate sync
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    setIntegrations(integrations.map(i =>
      i.id === id ? { ...i, syncStatus: 'success', lastSync: new Date().toISOString() } : i
    ));
    
    setSnackbar({ open: true, message: 'Sync completed successfully', severity: 'success' });
  };

  const handleSaveSyncSettings = () => {
    setSyncSettingsOpen(false);
    setSnackbar({ open: true, message: 'Sync settings saved', severity: 'success' });
  };

  const connectedCount = integrations.filter(i => i.connected).length;
  const storageIntegrations = integrations.filter(i => i.category === 'storage');
  const productivityIntegrations = integrations.filter(i => i.category === 'productivity');
  const socialIntegrations = integrations.filter(i => i.category === 'social');

  const renderIntegrationList = (items: Integration[], title: string) => (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1.5, fontWeight: 600, textTransform: 'uppercase', fontSize: '0.75rem' }}>
        {title}
      </Typography>
      <Paper variant="outlined" sx={{ borderRadius: 2 }}>
        <List disablePadding>
          {items.map((integration, index) => (
            <React.Fragment key={integration.id}>
              <ListItem
                sx={{ 
                  py: 2,
                  flexDirection: 'column',
                  alignItems: 'stretch',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <ListItemIcon sx={{ minWidth: 48 }}>
                    <Avatar sx={{ bgcolor: 'transparent', border: '1px solid', borderColor: 'divider' }}>
                      {integration.icon}
                    </Avatar>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {integration.name}
                        </Typography>
                        {integration.connected && (
                          <Chip
                            size="small"
                            icon={<CheckCircle sx={{ fontSize: 14 }} />}
                            label="Connected"
                            color="success"
                            sx={{ height: 22 }}
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          {integration.description}
                        </Typography>
                        {integration.connected && integration.connectedAccount && (
                          <Typography variant="caption" color="text.secondary">
                            Account: {integration.connectedAccount}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {integration.connected ? (
                      <>
                        {integration.syncStatus === 'syncing' ? (
                          <Chip
                            size="small"
                            icon={<Sync sx={{ fontSize: 14, animation: 'spin 1s linear infinite' }} />}
                            label="Syncing"
                            color="warning"
                          />
                        ) : integration.syncStatus === 'error' ? (
                          <Chip
                            size="small"
                            icon={<ErrorIcon sx={{ fontSize: 14 }} />}
                            label="Sync Error"
                            color="error"
                          />
                        ) : null}
                        <Tooltip title="Sync now">
                          <IconButton 
                            size="small" 
                            onClick={() => handleSync(integration.id)}
                            disabled={integration.syncStatus === 'syncing'}
                          >
                            <Refresh fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Settings">
                          <IconButton size="small" onClick={() => setSyncSettingsOpen(true)}>
                            <SettingsIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Disconnect">
                          <IconButton size="small" onClick={() => handleDisconnect(integration.id)} color="error">
                            <LinkOff fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    ) : (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleConnect(integration)}
                      >
                        Connect
                      </Button>
                    )}
                    <IconButton size="small" onClick={() => handleToggleExpand(integration.id)}>
                      {expandedId === integration.id ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                  </Box>
                </Box>
                
                <Collapse in={expandedId === integration.id}>
                  <Box sx={{ mt: 2, ml: 6, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                    {integration.connected && integration.lastSync && (
                      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Schedule sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">
                          Last synced: {new Date(integration.lastSync).toLocaleString()}
                        </Typography>
                      </Box>
                    )}
                    
                    <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                      Features:
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 2 }}>
                      {integration.features?.map(feature => (
                        <Chip key={feature} label={feature} size="small" variant="outlined" />
                      ))}
                    </Box>
                    
                    <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                      Required Permissions:
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {integration.permissions?.map(permission => (
                        <Chip 
                          key={permission} 
                          icon={<Security sx={{ fontSize: 14 }} />}
                          label={permission} 
                          size="small" 
                          sx={{ bgcolor: 'warning.light' }}
                        />
                      ))}
                    </Box>
                  </Box>
                </Collapse>
              </ListItem>
              {index < items.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>
      </Paper>
    </Box>
  );

  return (
    <Box>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: '8px' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* ── EXTENSIONS ─────────────────────────────────────────────────── */}
      {(activeSection === 'extensions' || activeSection === '') && (
        <>
          <Typography variant="h5" sx={{ fontWeight: 500, mb: 1 }}>Extensions</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Connect third-party services to enhance your email experience
          </Typography>

          {/* Summary Card */}
          <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2, bgcolor: 'action.hover' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                  {connectedCount} of {integrations.length} integrations connected
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Connect more services to unlock additional features
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Chip icon={<CalendarIcon />} label="Calendar Sync" color={syncSettings.syncCalendar ? 'success' : 'default'} />
                <Chip icon={<ContactsIcon />} label="Contacts Sync" color={syncSettings.syncContacts ? 'success' : 'default'} />
              </Box>
            </Box>
          </Paper>

          {renderIntegrationList(productivityIntegrations, 'Productivity')}
          {renderIntegrationList(storageIntegrations, 'Cloud Storage')}
          {renderIntegrationList(socialIntegrations, 'Social & Professional')}
        </>
      )}

      {/* ── DEVELOPER SPACE ────────────────────────────────────────────── */}
      {activeSection === 'developer' && <DeveloperSpace snackbar={snackbar} setSnackbar={setSnackbar} />}

      {/* ── CHANNELS ───────────────────────────────────────────────────── */}
      {activeSection === 'channels' && <ChannelsSection snackbar={snackbar} setSnackbar={setSnackbar} />}

      {/* OAuth Connect Dialog */}
      <Dialog open={connectDialogOpen} onClose={() => !isConnecting && setConnectDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          Connect {selectedIntegration?.name}
        </DialogTitle>
        <DialogContent>
          {isConnecting ? (
            <Box sx={{ py: 3, textAlign: 'center' }}>
              <LinearProgress sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary">
                Connecting to {selectedIntegration?.name}...
              </Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                <Avatar sx={{ width: 64, height: 64, bgcolor: 'transparent', border: '2px solid', borderColor: 'divider' }}>
                  {selectedIntegration?.icon}
                </Avatar>
              </Box>
              
              <Alert severity="info" sx={{ mb: 2 }}>
                You will be redirected to {selectedIntegration?.name} to authorize access.
              </Alert>
              
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Permissions requested:</Typography>
              <List dense>
                {selectedIntegration?.permissions?.map(permission => (
                  <ListItem key={permission} sx={{ py: 0 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckCircle sx={{ fontSize: 16 }} color="success" />
                    </ListItemIcon>
                    <ListItemText primary={permission} />
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConnectDialogOpen(false)} disabled={isConnecting}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleOAuthConnect} disabled={isConnecting}>
            {isConnecting ? 'Connecting...' : 'Authorize'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* API Key Dialog */}
      <Dialog open={apiKeyDialogOpen} onClose={() => !isConnecting && setApiKeyDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Key />
            Connect {selectedIntegration?.name}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter your {selectedIntegration?.name} personal access token to connect.
          </Typography>
          
          <TextField
            fullWidth
            label="API Key / Personal Access Token"
            type="password"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder="ghp_xxxxxxxxxxxx"
            helperText={`Generate a token in your ${selectedIntegration?.name} settings`}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApiKeyDialogOpen(false)} disabled={isConnecting}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleApiKeyConnect} 
            disabled={isConnecting || !apiKey}
          >
            {isConnecting ? 'Connecting...' : 'Connect'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sync Settings Dialog */}
      <Dialog open={syncSettingsOpen} onClose={() => setSyncSettingsOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Sync Settings</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={syncSettings.syncContacts}
                  onChange={(e) => setSyncSettings({ ...syncSettings, syncContacts: e.target.checked })}
                />
              }
              label="Sync contacts"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={syncSettings.syncCalendar}
                  onChange={(e) => setSyncSettings({ ...syncSettings, syncCalendar: e.target.checked })}
                />
              }
              label="Sync calendar events"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={syncSettings.syncDrive}
                  onChange={(e) => setSyncSettings({ ...syncSettings, syncDrive: e.target.checked })}
                />
              }
              label="Enable cloud drive access"
            />
            
            <Divider sx={{ my: 2 }} />
            
            <FormControlLabel
              control={
                <Checkbox
                  checked={syncSettings.autoSync}
                  onChange={(e) => setSyncSettings({ ...syncSettings, autoSync: e.target.checked })}
                />
              }
              label="Enable automatic sync"
            />
            
            {syncSettings.autoSync && (
              <TextField
                fullWidth
                type="number"
                label="Sync interval (minutes)"
                value={syncSettings.syncInterval}
                onChange={(e) => setSyncSettings({ ...syncSettings, syncInterval: parseInt(e.target.value) || 15 })}
                size="small"
                sx={{ mt: 1 }}
                inputProps={{ min: 5, max: 60 }}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSyncSettingsOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveSyncSettings}>Save</Button>
        </DialogActions>
      </Dialog>

      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </Box>
  );
};

export default IntegrationsSettings;
